// Fincan + değirmen sensörü — renk kuralı ve son okumaları CSV indirme
// (Bakım sekmesi). Sözleşme: BayinerIOT-Workspace- docs/api-contracts/
// cup-grinder-sensor-live.md (#132). Liste useCupGrinderSensorLive'da
// tutulur, burada yalnız değerlendirme ve CSV üretilir.

export const CUP_GRINDER_HISTORY_LIMIT = 50;

// Kart eşikleri (bilgi amaçlı; yük cupThreshold/grinderThreshold taşır).
export const DEFAULT_CUP_THRESHOLD = -250;
export const DEFAULT_GRINDER_THRESHOLD = -110;
// Eşiğe bundan yakın değer "sınırda" sayılır (turuncu).
export const EDGE_MARGIN = 50;
// Kart değeri bundan yaşlıysa veri yok sayılır (server/tablet 5 sn kuralı).
export const VALUE_STALE_MS = 5000;

const isNum = (v) => typeof v === "number" && Number.isFinite(v);

/** Sensör değeri var mı (diff sayı ve bayat değil). */
export const hasValue = (s) =>
  !!s && isNum(s.diff) && !(isNum(s.ageMs) && s.ageMs >= VALUE_STALE_MS);

/**
 * Kart rengi: "none" = veri yok (gri), "edge" = eşiğe 50'den yakın
 * (turuncu, sınırda), "present" = fincan var (yeşil), "absent" = nötr.
 */
export const sensorTone = (s, threshold) => {
  if (!hasValue(s)) return "none";
  if (isNum(threshold) && Math.abs(s.diff - threshold) < EDGE_MARGIN)
    return "edge";
  return s.present === 1 ? "present" : "absent";
};

/** Eşiğe uzaklık (diff - threshold); veri yoksa null. */
export const thresholdDistance = (s, threshold) =>
  hasValue(s) && isNum(threshold) ? s.diff - threshold : null;

export const cupPresenceText = (s) =>
  !hasValue(s) || (s.present !== 0 && s.present !== 1)
    ? "Veri yok"
    : s.present === 1
      ? "Fincan var"
      : "Fincan yok";

export const grinderPresenceText = (s) =>
  !hasValue(s) || (s.present !== 0 && s.present !== 1)
    ? "Veri yok"
    : s.present === 1
      ? "Ağızda fincan var"
      : "Ağızda fincan yok";

const SOURCE_TEXT = { drawer: "Makine ekranı", remote: "Panel" };

const pad = (n, w = 2) => String(n).padStart(w, "0");

// Yerel saat, milisaniyeli — Excel'de sıralanabilir.
const formatTime = (value) => {
  if (value == null) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ` +
    `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}.` +
    pad(d.getMilliseconds(), 3)
  );
};

// Türkçe Excel ayırıcısı ";" — hücrede ; " ya da satır sonu varsa tırnakla.
const cell = (v) => {
  const s = v === null || v === undefined ? "" : String(v);
  return /[;"\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const cupsOf = (data) => (Array.isArray(data?.cups) ? data.cups : []);

/**
 * @param {{ receivedAt: number, data: object }[]} entries eskiden yeniye
 * @returns {string} UTF-8 BOM'lu, ";" ayırıcılı CSV
 */
export function buildCupGrinderHistoryCsv(entries) {
  // Göz sayısı: kayıtlardaki en büyük göz numarası (aktif göz sayısı).
  const cupCount = entries.reduce(
    (max, e) =>
      cupsOf(e.data).reduce(
        (m, c) => (isNum(c?.cup) && c.cup > m ? c.cup : m),
        max,
      ),
    0,
  );
  const cupNos = Array.from({ length: cupCount }, (_, i) => i + 1);
  const header = ["Zaman"];
  cupNos.forEach((n) => header.push(`Göz ${n} fark`, `Göz ${n} durum`));
  header.push("Değirmen fark", "Değirmen durum", "Kaynak");

  const lines = [header.map(cell).join(";")];
  entries.forEach((e) => {
    const data = e.data || {};
    const row = [formatTime(e.receivedAt)];
    cupNos.forEach((n) => {
      const c = cupsOf(data).find((x) => x?.cup === n);
      row.push(c && hasValue(c) ? c.diff : "", c ? cupPresenceText(c) : "");
    });
    const g = data.grinder;
    row.push(
      g && hasValue(g) ? g.diff : "",
      g ? grinderPresenceText(g) : "",
      SOURCE_TEXT[data.source] || data.source,
    );
    lines.push(row.map(cell).join(";"));
  });
  // BOM: Excel UTF-8'i tanısın (Türkçe karakterler bozulmasın).
  return `﻿${lines.join("\r\n")}\r\n`;
}

export function cupGrinderHistoryFileName(serialNo, now = new Date()) {
  const stamp =
    `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-` +
    `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
  const sn = String(serialNo || "cihaz").replace(/[^\w-]+/g, "_");
  return `fincan-degirmen-sensoru_${sn}_${stamp}.csv`;
}

export function downloadCupGrinderHistory(entries, serialNo) {
  const blob = new Blob([buildCupGrinderHistoryCsv(entries)], {
    type: "text/csv;charset=utf-8",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = cupGrinderHistoryFileName(serialNo);
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
